// batch-63 certificaat-mailbox (FO-469) — certificaten automatisch inlezen uit de
// certificaat-mailbox, met de bestaande HR-goedkeuringsstap (spec 37).
//
// Gedeelde DTO's, statussen en request-schemas (API <-> backoffice). Eén bron: de
// API parseert ermee én de backoffice-formulieren valideren er client-side mee
// vóór verzenden (CLAUDE.md §Validatie). Het ruwe AI-antwoord heeft een eigen,
// bewust ruim schema (`CertificaatDocumentExtractieRuwSchema`); de API normaliseert
// dat naar `CertificaatExtractieDTO` voordat er iets mee gematcht wordt.

import { z } from 'zod'
import { CreateCertificateTypeSchema } from './certificate-types'

// ─── Statussen + labels ──────────────────────────────────────────────────────

export const CERTIFICAAT_INGEST_STATUSSEN = [
  'NIEUW',
  'VERWERKT',
  'REVIEW',
  'FOUT',
  'GENEGEERD',
  'QUARANTAINE',
] as const
export type CertificaatIngestStatus = (typeof CERTIFICAAT_INGEST_STATUSSEN)[number]

export const CERTIFICAAT_INGEST_STATUS_LABELS: Record<CertificaatIngestStatus, string> = {
  NIEUW: 'Wacht op verwerking',
  VERWERKT: 'Verwerkt',
  REVIEW: 'Handmatige controle',
  FOUT: 'Fout bij verwerken',
  GENEGEERD: 'Genegeerd',
  QUARANTAINE: 'Quarantaine (malware-scan)',
}

export const CERTIFICAAT_INGEST_ITEM_STATUSSEN = ['OPEN', 'AANVRAAG', 'GEKOPPELD', 'GENEGEERD'] as const
export type CertificaatIngestItemStatus = (typeof CERTIFICAAT_INGEST_ITEM_STATUSSEN)[number]

export const CERTIFICAAT_INGEST_ITEM_STATUS_LABELS: Record<CertificaatIngestItemStatus, string> = {
  OPEN: 'Te controleren',
  AANVRAAG: 'Aanvraag aangemaakt',
  GEKOPPELD: 'Bij bestaand certificaat gevoegd',
  GENEGEERD: 'Genegeerd',
}

export const CERTIFICAAT_REVIEW_REDENEN = [
  'MEDEWERKER_ONBEKEND',
  'MEDEWERKER_MEERDERE',
  'MEDEWERKER_NIET_ACTIEF',
  'MEDEWERKER_ONZEKER',
  'TYPE_ONBEKEND',
  'TYPE_MEERDERE',
  'DATUM_ONTBREEKT',
  'DATUM_ONZEKER',
  'GEEN_GELDIGHEID',
  'BIJLAGE_ONLEESBAAR',
  'GEEN_CERTIFICAAT',
  'LINK_ZONDER_BIJLAGE',
  'NIET_ONDERSTEUNDE_BIJLAGE',
  'MOGELIJK_DUBBEL',
  'BUNDEL_NIET_SPLITSBAAR',
  'DUBBEL_BESTAND',
  'TE_VEEL_BIJLAGEN',
  'MAIL_NIET_OPGEHAALD',
] as const
export type CertificaatReviewReden = (typeof CERTIFICAAT_REVIEW_REDENEN)[number]

export const CERTIFICAAT_REVIEW_REDEN_LABELS: Record<CertificaatReviewReden, string> = {
  MEDEWERKER_ONBEKEND: 'Geen medewerker gevonden',
  MEDEWERKER_MEERDERE: 'Naam past op meerdere medewerkers',
  MEDEWERKER_NIET_ACTIEF: 'Geen actieve medewerker',
  MEDEWERKER_ONZEKER: 'Medewerker niet zeker',
  TYPE_ONBEKEND: 'Certificaattype onbekend',
  TYPE_MEERDERE: 'Certificaattype niet eenduidig',
  DATUM_ONTBREEKT: 'Uitgiftedatum ontbreekt',
  DATUM_ONZEKER: 'Datum niet zeker gelezen',
  GEEN_GELDIGHEID: 'Geen vervaldatum of geldigheidsduur',
  BIJLAGE_ONLEESBAAR: 'Bijlage niet leesbaar',
  GEEN_CERTIFICAAT: 'Geen certificaat herkend',
  LINK_ZONDER_BIJLAGE: 'Link zonder bijlage (handmatig ophalen)',
  NIET_ONDERSTEUNDE_BIJLAGE: 'Niet-ondersteunde bijlage',
  MOGELIJK_DUBBEL: 'Mogelijk dubbel',
  BUNDEL_NIET_SPLITSBAAR: 'Bundel niet per deelnemer te splitsen',
  DUBBEL_BESTAND: 'Zelfde bestand al verwerkt',
  TE_VEEL_BIJLAGEN: 'Meer bijlagen dan automatisch verwerkt',
  MAIL_NIET_OPGEHAALD: 'Mail kon niet worden opgehaald',
}

/** Uitleg per reden: wat HR in de review-lijst moet doen. */
export const CERTIFICAAT_REVIEW_REDEN_UITLEG: Record<CertificaatReviewReden, string> = {
  MEDEWERKER_ONBEKEND:
    'De naam op het certificaat past op geen enkele medewerker. Kies de juiste medewerker of negeer het certificaat.',
  MEDEWERKER_MEERDERE:
    'De naam past op meer dan één medewerker. Er is bewust niets gekozen: kies zelf de juiste medewerker.',
  MEDEWERKER_NIET_ACTIEF:
    'De naam past alleen op een oud-medewerker. Er wordt nooit automatisch aan een naamgenoot gekoppeld.',
  MEDEWERKER_ONZEKER:
    'Er is alleen een zwakke naam-match zonder bevestiging (geboortedatum, afzender of cursusdeelname). Controleer de medewerker.',
  TYPE_ONBEKEND:
    'De cursusnaam is niet herkend. Koppel een bestaand type of maak een nieuw type aan; de naam wordt als synoniem onthouden.',
  TYPE_MEERDERE: 'De cursusnaam past op meer dan één certificaattype. Kies het juiste type.',
  DATUM_ONTBREEKT: 'Er is geen uitgiftedatum gelezen. Vul de datums handmatig in.',
  DATUM_ONZEKER: 'Een datum is met lage zekerheid gelezen. Controleer de datums tegen het document.',
  GEEN_GELDIGHEID:
    'Er staat geen vervaldatum op het certificaat en het type kent geen standaard geldigheidsduur. Vul de vervaldatum in of laat hem bewust leeg.',
  BIJLAGE_ONLEESBAAR: 'De bijlage kon niet automatisch gelezen worden. Vul de gegevens handmatig in.',
  GEEN_CERTIFICAAT: 'In de bijlage is geen certificaat herkend. Controleer de mail of negeer hem.',
  LINK_ZONDER_BIJLAGE:
    'De mail bevat geen bijlage maar wel een link. Haal het certificaat handmatig op en vul het in.',
  NIET_ONDERSTEUNDE_BIJLAGE:
    'De mail bevat een bijlage die niet automatisch verwerkt wordt (alleen PDF en afbeeldingen). Controleer de mail.',
  MOGELIJK_DUBBEL:
    'Dit certificaat lijkt op een bestaand certificaat of een openstaande aanvraag. Kies koppelen, toch indienen of negeren.',
  BUNDEL_NIET_SPLITSBAAR:
    'Het document bevat meerdere deelnemers maar kon niet per deelnemer gesplitst worden. Het bestand wordt daarom niet automatisch bij de medewerker gezet.',
  DUBBEL_BESTAND: 'Exact hetzelfde bestand is al eerder uit de mailbox verwerkt.',
  TE_VEEL_BIJLAGEN:
    'De mail heeft meer bijlagen dan er per mail automatisch verwerkt worden. Open de mail in de mailbox en vul de overige certificaten handmatig in.',
  MAIL_NIET_OPGEHAALD:
    'De mail kon na meerdere pogingen niet uit de mailbox worden opgehaald. Open hem in de mailbox en vul het certificaat handmatig in, of negeer hem.',
}

export const EXTRACTIE_ZEKERHEDEN = ['HOOG', 'MIDDEL', 'LAAG'] as const
export type ExtractieZekerheid = (typeof EXTRACTIE_ZEKERHEDEN)[number]

export const EXTRACTIE_ZEKERHEID_LABELS: Record<ExtractieZekerheid, string> = {
  HOOG: 'Zeker',
  MIDDEL: 'Redelijk zeker',
  LAAG: 'Onzeker',
}

// ─── Ruw AI-antwoord (bewust ruim) ───────────────────────────────────────────

/** Zekerheid tolerant parsen ("hoog", "High", 0.9 ...); onbekend -> null. */
const ruweZekerheid = z.preprocess((v) => {
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return null
    if (v >= 0.8) return 'HOOG'
    if (v >= 0.5) return 'MIDDEL'
    return 'LAAG'
  }
  if (typeof v !== 'string') return null
  const t = v.trim().toUpperCase()
  if (t === 'HOOG' || t === 'HIGH') return 'HOOG'
  if (t === 'MIDDEL' || t === 'MEDIUM' || t === 'GEMIDDELD') return 'MIDDEL'
  if (t === 'LAAG' || t === 'LOW') return 'LAAG'
  return null
}, z.enum(EXTRACTIE_ZEKERHEDEN).nullable())

const ruweTekst = z.preprocess(
  (v) => (typeof v === 'number' ? String(v) : v),
  z.string().nullish(),
)

const ruwGetal = z.preprocess((v) => {
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.trim().replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }
  return v
}, z.number().nullish())

/**
 * Eén certificaat zoals Claude het teruggeeft. Een bundel-PDF levert er meerdere
 * op (één per deelnemer). Alles is optioneel: de API normaliseert en valideert
 * datums zelf (kalender-geldig ISO), nooit blind overnemen.
 */
export const CertificaatExtractieRuwSchema = z.object({
  /** 1-based pagina's van dit certificaat binnen de PDF. */
  paginas: z.array(ruwGetal).nullish(),
  deelnemerNaam: ruweTekst,
  geboortedatum: ruweTekst,
  certificaatNaam: ruweTekst,
  /** Exacte typenaam uit de meegegeven lijst, of null. Alleen een suggestie. */
  typeSuggestie: ruweTekst,
  opleider: ruweTekst,
  uitgever: ruweTekst,
  uitgifteDatum: ruweTekst,
  geldigVanaf: ruweTekst,
  vervalDatum: ruweTekst,
  geldigheidsduurMaanden: ruwGetal,
  certificaatNummer: ruweTekst,
  zekerheid: z
    .object({
      deelnemerNaam: ruweZekerheid.optional(),
      geboortedatum: ruweZekerheid.optional(),
      certificaatNaam: ruweZekerheid.optional(),
      opleider: ruweZekerheid.optional(),
      uitgever: ruweZekerheid.optional(),
      uitgifteDatum: ruweZekerheid.optional(),
      geldigVanaf: ruweZekerheid.optional(),
      vervalDatum: ruweZekerheid.optional(),
      geldigheidsduurMaanden: ruweZekerheid.optional(),
      certificaatNummer: ruweZekerheid.optional(),
    })
    .partial()
    .nullish(),
})
export type CertificaatExtractieRuw = z.infer<typeof CertificaatExtractieRuwSchema>

export const CERTIFICAAT_DOCUMENT_SOORTEN = ['CERTIFICAAT', 'GEEN_CERTIFICAAT', 'ONLEESBAAR'] as const
export type CertificaatDocumentSoort = (typeof CERTIFICAAT_DOCUMENT_SOORTEN)[number]

/** Het volledige AI-antwoord per bijlage: soort + array (bundel = meerdere). */
export const CertificaatDocumentExtractieRuwSchema = z.object({
  documentSoort: z.preprocess(
    (v) => (typeof v === 'string' ? v.trim().toUpperCase() : v),
    z.enum(CERTIFICAAT_DOCUMENT_SOORTEN).catch('ONLEESBAAR'),
  ),
  certificaten: z.array(CertificaatExtractieRuwSchema).nullish(),
})
export type CertificaatDocumentExtractieRuw = z.infer<typeof CertificaatDocumentExtractieRuwSchema>

// ─── Genormaliseerde extractie (opgeslagen op het item) ─────────────────────

export interface CertificaatExtractieVeld<T> {
  waarde: T | null
  zekerheid: ExtractieZekerheid | null
}

/** Geëxtraheerde velden van één certificaat, na normalisatie door de API. */
export interface CertificaatExtractieDTO {
  deelnemerNaam: CertificaatExtractieVeld<string>
  /** yyyy-mm-dd */
  geboortedatum: CertificaatExtractieVeld<string>
  certificaatNaam: CertificaatExtractieVeld<string>
  /** Door de AI voorgestelde typenaam uit de lijst. Nooit automatisch toegepast. */
  typeSuggestie: string | null
  opleider: CertificaatExtractieVeld<string>
  uitgever: CertificaatExtractieVeld<string>
  /** yyyy-mm-dd */
  uitgifteDatum: CertificaatExtractieVeld<string>
  /** yyyy-mm-dd */
  geldigVanaf: CertificaatExtractieVeld<string>
  /** yyyy-mm-dd */
  vervalDatum: CertificaatExtractieVeld<string>
  geldigheidsduurMaanden: CertificaatExtractieVeld<number>
  certificaatNummer: CertificaatExtractieVeld<string>
  /** 1-based pagina's in de bron-PDF (leeg = hele document/onbekend). */
  paginas: number[]
}

// ─── Match-onderbouwing (opgeslagen op het item) ────────────────────────────

export const MEDEWERKER_MATCH_NIVEAUS = ['EXACT', 'STERK', 'ZWAK'] as const
export type MedewerkerMatchNiveau = (typeof MEDEWERKER_MATCH_NIVEAUS)[number]

export const MEDEWERKER_MATCH_NIVEAU_LABELS: Record<MedewerkerMatchNiveau, string> = {
  EXACT: 'Volledige naam gelijk',
  STERK: 'Voornaam of voorletter + achternaam gelijk',
  ZWAK: 'Alleen achternaam of een kleine spelvariant',
}

export type MedewerkerMatchHint = 'AFZENDER' | 'DEELNAME' | 'GEBOORTEDATUM'

export const MEDEWERKER_MATCH_HINT_LABELS: Record<MedewerkerMatchHint, string> = {
  AFZENDER: 'mailde zelf',
  DEELNAME: 'volgde recent deze cursus',
  GEBOORTEDATUM: 'geboortedatum klopt',
}

export type MedewerkerMatchUitkomst = 'GEVONDEN' | 'ONBEKEND' | 'MEERDERE' | 'NIET_ACTIEF' | 'ONZEKER'

export interface MedewerkerMatchKandidaatDTO {
  employeeId: string
  naam: string
  employeeNumber: string | null
  niveau: MedewerkerMatchNiveau
  actief: boolean
  hints: MedewerkerMatchHint[]
}

export type CertificaatTypeMatchUitkomst = 'GEVONDEN' | 'ONBEKEND' | 'MEERDERE'
export type CertificaatTypeMatchVia = 'NAAM' | 'NAAMDEEL' | 'CODE' | 'SYNONIEM'

export const CERTIFICAAT_TYPE_MATCH_VIA_LABELS: Record<CertificaatTypeMatchVia, string> = {
  NAAM: 'naam van het type',
  NAAMDEEL: 'deel van de typenaam',
  CODE: 'code van het type',
  SYNONIEM: 'synoniem',
}

export type OpleiderMatchVia = 'NAAM' | 'AFZENDERDOMEIN'

export type VervaldatumBron = 'DOCUMENT' | 'DUUR_DOCUMENT' | 'DUUR_TYPE' | 'GEEN'

export const VERVALDATUM_BRON_LABELS: Record<VervaldatumBron, string> = {
  DOCUMENT: 'van het certificaat',
  DUUR_DOCUMENT: 'afgeleid uit de geldigheidsduur op het certificaat',
  DUUR_TYPE: 'afgeleid uit de standaard geldigheidsduur van het type',
  GEEN: 'geen vervaldatum',
}

export const DUBBEL_REDENEN = [
  'NUMMER',
  'BESTAND',
  'UITGIFTEDATUM',
  'VERVALDATUM',
  'OUDER_DAN_BESTAAND',
  'OPEN_AANVRAAG',
] as const
export type DubbelReden = (typeof DUBBEL_REDENEN)[number]

export const DUBBEL_REDEN_LABELS: Record<DubbelReden, string> = {
  NUMMER: 'zelfde certificaatnummer',
  BESTAND: 'zelfde bestand',
  UITGIFTEDATUM: 'zelfde uitgiftedatum',
  VERVALDATUM: 'zelfde vervaldatum',
  OUDER_DAN_BESTAAND: 'ouder dan het bestaande certificaat',
  OPEN_AANVRAAG: 'er staat al een aanvraag open',
}

export interface MogelijkDubbelDTO {
  soort: 'CERTIFICAAT' | 'AANVRAAG'
  id: string
  redenen: DubbelReden[]
  certificateTypeNaam: string | null
  /** ISO */
  issuedAt: string | null
  /** ISO */
  expiresAt: string | null
  certificaatNummer: string | null
  /** Certificaat: lifecycle; aanvraag: status. */
  status: string
}

export interface CertificaatMatchOnderbouwing {
  medewerker: {
    uitkomst: MedewerkerMatchUitkomst
    toelichting: string
    kandidaten: MedewerkerMatchKandidaatDTO[]
  }
  type: {
    uitkomst: CertificaatTypeMatchUitkomst
    via: CertificaatTypeMatchVia | null
    toelichting: string
    kandidaatIds: string[]
    /** Type dat de AI voorstelde (alleen een hint in de review). */
    aiSuggestieTypeId: string | null
  }
  opleider: {
    opleiderId: string | null
    naam: string | null
    via: OpleiderMatchVia | null
  }
  datums: {
    vervaldatumBron: VervaldatumBron
    toelichting: string
  }
  dubbel: MogelijkDubbelDTO[]
  deelname: {
    deelnameId: string
    opleidingNaam: string
    /** ISO */
    afgerondOp: string | null
  } | null
  /** Keuzes die HR in de review-lijst maakte; die overrulen een herbeoordeling. */
  handmatig?: {
    employeeId: string | null
    certificateTypeId: string | null
  }
}

// ─── Lijst- en detail-DTO's ─────────────────────────────────────────────────

export interface CertificaatReviewItemRijDTO {
  id: string
  ingestId: string
  status: CertificaatIngestItemStatus
  redenen: CertificaatReviewReden[]
  /** ISO */
  ontvangenOp: string | null
  afzender: string | null
  onderwerp: string | null
  bijlageNaam: string | null
  deelnemerNaam: string | null
  cursusnaam: string | null
  voorgesteldeMedewerker: { id: string; naam: string } | null
  voorgesteldType: { id: string; naam: string } | null
  aanvraagId: string | null
  /** ISO */
  createdAt: string
}

export interface CertificaatReviewLijstMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Teller over de VOLLEDIGE set (niet over de geladen pagina). */
  aantalOpen: number
}

export interface CertificaatIngestRijDTO {
  id: string
  status: CertificaatIngestStatus
  afzender: string | null
  onderwerp: string | null
  /** ISO */
  ontvangenOp: string | null
  aantalBijlagen: number
  aantalItems: number
  aantalOpen: number
  aantalAanvragen: number
  foutmelding: string | null
  notitie: string | null
  genegeerdReden: string | null
  /** Een mislukte mail die HR opnieuw kan laten ophalen of verwerken. */
  kanOpnieuw: boolean
  /** ISO */
  createdAt: string
}

export interface NietOndersteundeBijlageDTO {
  naam: string
  type: string | null
  grootte: number | null
}

export interface CertificaatReviewVoorstelDTO {
  employeeId: string | null
  certificateTypeId: string | null
  /** ISO */
  issuedAt: string | null
  /** ISO */
  geldigVanaf: string | null
  /** ISO */
  expiresAt: string | null
  expiresAfgeleid: boolean
  certificaatNummer: string | null
  uitgever: string | null
  opleidingDeelnameId: string | null
}

export interface CertificaatReviewItemDetailDTO extends CertificaatReviewItemRijDTO {
  extractie: CertificaatExtractieDTO | null
  onderbouwing: CertificaatMatchOnderbouwing | null
  voorstel: CertificaatReviewVoorstelDTO
  /** Er is een (per deelnemer gesplitst) document dat bij een aanvraag mag. */
  heeftDocument: boolean
  documentMimeType: string | null
  /** Type van de volledige bijlage uit de mail (uit de bytes bepaald). */
  bijlageMimeType: string | null
  /** De bron-bijlage bevat meer dan dit ene certificaat (bundel). */
  isBundelDeel: boolean
  links: string[]
  nietOndersteund: NietOndersteundeBijlageDTO[]
  genegeerdReden: string | null
  /** ISO */
  afgehandeldOp: string | null
  afgehandeldDoorNaam: string | null
  gekoppeldCertificaatId: string | null
  dubbelVanItemId: string | null
  ingest: {
    id: string
    status: CertificaatIngestStatus
    mailbox: string
    afzender: string | null
    onderwerp: string | null
    /** ISO */
    ontvangenOp: string | null
    aantalBijlagen: number
    notitie: string | null
    foutmelding: string | null
  }
  /** Certificaten van de voorgestelde medewerker (voor "bij bestaand certificaat voegen"). */
  bestaandeCertificaten: Array<{
    id: string
    certificateTypeNaam: string
    /** ISO */
    issuedAt: string
    /** ISO */
    expiresAt: string | null
    certificaatNummer: string | null
    status: string
  }>
  /** Er zijn meer certificaten dan getoond (de lijst is begrensd). */
  bestaandeCertificatenAfgekapt: boolean
  /** Andere items uit dezelfde mail (bundel/meerdere bijlagen). */
  andereItems: Array<{
    id: string
    status: CertificaatIngestItemStatus
    deelnemerNaam: string | null
    cursusnaam: string | null
  }>
  /** Er zijn meer items in deze mail dan getoond. */
  andereItemsAfgekapt: boolean
}

/** Status van de mailbox-koppeling (voor HR en de mail-instellingen). */
export interface CertificaatMailboxStatusDTO {
  /** Het adres dat werkelijk gepold wordt (instelling, anders omgeving), of null. */
  effectiefAdres: string | null
  adresBron: 'INSTELLING' | 'OMGEVING' | 'GEEN'
  /** Wordt er gepold (adres + actief + geen kill-switch)? */
  ingeschakeld: boolean
  /** ISO */
  laatstePollOp: string | null
  /** ISO */
  laatsteFoutOp: string | null
  laatsteFout: string | null
  /** Aantal mails dat nog op verwerking wacht (bv. tijdens een AI-pauze). */
  wachtOpVerwerking: number
  aiPauzeActief: boolean
}

export interface CertificaatMailboxInstellingenDTO {
  /** In de mail-instellingen ingesteld adres (null = val terug op de omgeving). */
  mailbox: string | null
  actief: boolean
  terugkoppelingIntern: boolean
  terugkoppelingExtern: boolean
  interneDomeinen: string[]
  status: CertificaatMailboxStatusDTO
}

export interface CertificaatSynoniemDTO {
  id: string
  certificateTypeId: string
  certificateTypeNaam: string
  naam: string
  sleutel: string
  bron: 'HANDMATIG' | 'REVIEW' | 'STANDAARD'
  /** ISO */
  createdAt: string
}

// ─── Request-schemas ─────────────────────────────────────────────────────────

const isoDag = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}/, 'Ongeldige datum')
  .refine((s) => !Number.isNaN(Date.parse(s.slice(0, 10))), 'Ongeldige datum')

export const CertificaatReviewNegeerSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden van minimaal 5 tekens')
    .max(1000, 'Maximaal 1000 tekens'),
})
export type CertificaatReviewNegeerInput = z.infer<typeof CertificaatReviewNegeerSchema>

export const CertificaatReviewKiesMedewerkerSchema = z.object({
  employeeId: z.string().trim().min(1, 'Kies een medewerker'),
})
export type CertificaatReviewKiesMedewerkerInput = z.infer<typeof CertificaatReviewKiesMedewerkerSchema>

const DOMEIN_RE = /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/

/**
 * Een nieuw certificaattype vanuit de review: exact dezelfde veldregels als het
 * gewone aanmaken (`CreateCertificateTypeSchema`), zodat er maar één catalogus-
 * regel bestaat.
 */
export const CertificaatNieuwTypeSchema = z.object({
  naam: CreateCertificateTypeSchema.shape.name,
  code: CreateCertificateTypeSchema.shape.code,
  validityMonths: CreateCertificateTypeSchema.shape.validityMonths,
})
export type CertificaatNieuwTypeInput = z.infer<typeof CertificaatNieuwTypeSchema>

/**
 * Type koppelen in de review: een bestaand type kiezen óf een nieuw type
 * aanmaken. De gelezen cursusnaam wordt (default) als synoniem onthouden, zodat
 * de volgende mail met dezelfde naam automatisch herkend wordt.
 */
export const CertificaatReviewKoppelTypeSchema = z
  .object({
    certificateTypeId: z.string().trim().min(1).optional(),
    nieuwType: CertificaatNieuwTypeSchema.optional(),
    onthoudSynoniem: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    const aantal = (v.certificateTypeId ? 1 : 0) + (v.nieuwType ? 1 : 0)
    if (aantal !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['certificateTypeId'],
        message: 'Kies een bestaand type of maak een nieuw type aan',
      })
    }
  })
export type CertificaatReviewKoppelTypeInput = z.infer<typeof CertificaatReviewKoppelTypeSchema>

/**
 * Handmatig invullen / aanvraag aanmaken vanuit een review-item. Het bestand uit
 * de mail gaat mee zolang het een per deelnemer gesplitst document is; een extra
 * bestand (bv. handmatig opgehaald via een link) loopt via de multipart-variant.
 */
export const CertificaatReviewAanvraagSchema = z
  .object({
    employeeId: z.string().trim().min(1, 'Kies een medewerker'),
    certificateTypeId: z.string().trim().min(1, 'Kies een certificaattype'),
    issuedAt: isoDag,
    geldigVanaf: isoDag.nullable().optional(),
    expiresAt: isoDag.nullable().optional(),
    certificaatNummer: z.string().trim().max(120, 'Maximaal 120 tekens').nullable().optional(),
    uitgever: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
    notes: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
    /** Bestand uit de mail meenemen (alleen mogelijk als het niet een ongesplitste bundel is). */
    bestandMeenemen: z.boolean().optional(),
    /** Bewust doorgaan ondanks "mogelijk dubbel". */
    bevestigDubbel: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.expiresAt && v.expiresAt.slice(0, 10) < v.issuedAt.slice(0, 10)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresAt'],
        message: 'De vervaldatum ligt vóór de uitgiftedatum',
      })
    }
    if (v.geldigVanaf && v.expiresAt && v.expiresAt.slice(0, 10) < v.geldigVanaf.slice(0, 10)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresAt'],
        message: 'De vervaldatum ligt vóór de ingangsdatum',
      })
    }
  })
export type CertificaatReviewAanvraagInput = z.infer<typeof CertificaatReviewAanvraagSchema>

/** Bestand bij een bestaand certificaat voegen (dubbel-verdacht item). */
export const CertificaatReviewKoppelCertificaatSchema = z.object({
  employeeCertificateId: z.string().trim().min(1, 'Kies een certificaat'),
})
export type CertificaatReviewKoppelCertificaatInput = z.infer<
  typeof CertificaatReviewKoppelCertificaatSchema
>

/** Hele mail negeren (bv. geen certificaat, spam). */
export const CertificaatIngestNegeerSchema = CertificaatReviewNegeerSchema
export type CertificaatIngestNegeerInput = CertificaatReviewNegeerInput

export const CertificaatSynoniemCreateSchema = z.object({
  certificateTypeId: z.string().trim().min(1, 'Kies een certificaattype'),
  naam: z.string().trim().min(2, 'Minimaal 2 tekens').max(200, 'Maximaal 200 tekens'),
})
export type CertificaatSynoniemCreateInput = z.infer<typeof CertificaatSynoniemCreateSchema>

export const CertificaatMailboxInstellingenSchema = z.object({
  /** Leeg/null = geen eigen adres; de api valt dan terug op CERTIFICAAT_MAILBOX. */
  mailbox: z
    .string()
    .trim()
    .max(254, 'Maximaal 254 tekens')
    .email('Geen geldig e-mailadres')
    .nullable(),
  actief: z.boolean(),
  terugkoppelingIntern: z.boolean(),
  terugkoppelingExtern: z.boolean(),
  interneDomeinen: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .regex(DOMEIN_RE, 'Geen geldig domein (bijvoorbeeld streep.nl)'),
    )
    .max(20, 'Maximaal 20 domeinen'),
})
export type CertificaatMailboxInstellingenInput = z.infer<typeof CertificaatMailboxInstellingenSchema>

/** Defaults: terugkoppeling aan voor interne afzenders, uit voor externe opleiders. */
export const CERTIFICAAT_MAILBOX_INSTELLINGEN_DEFAULT: CertificaatMailboxInstellingenInput = {
  mailbox: null,
  actief: true,
  terugkoppelingIntern: true,
  terugkoppelingExtern: false,
  interneDomeinen: ['streep.nl'],
}

/**
 * Standaard-synoniemen die de api eenmalig lazy borgt (prod draait geen seeds).
 * Een door HR verwijderd synoniem komt niet terug.
 */
export const CERTIFICAAT_STANDAARD_SYNONIEMEN: ReadonlyArray<{
  typeNaam: string
  synoniemen: readonly string[]
}> = [
  { typeNaam: 'VCA Basis', synoniemen: ['B-VCA', 'VCA-B', 'BVCA', 'VCA basisveiligheid', 'Basisveiligheid VCA'] },
  { typeNaam: 'VCA Vol', synoniemen: ['VOL-VCA', 'VCA-VOL', 'VVCA', 'Veiligheid voor Operationeel Leidinggevenden'] },
  // batch-71 verplichte-certificaten (FO-541 §A2). Alleen namen die naar EEN type wijzen: een
  // kale "NEN 3140", "IPAF" of "Hoogwerker" past bij meerdere varianten en wordt daarom geen
  // synoniem (certificaatSynoniemTeAlgemeen); die keuze ligt bij de medewerker of HR.
  { typeNaam: 'NEN 3140 VP - Vakbekwaam persoon', synoniemen: ['NEN 3140 VP', 'NEN3140 VP', 'NEN3140-VP', 'Vakbekwaam persoon NEN 3140'] },
  { typeNaam: 'NEN 3140 VOP - Voldoende onderricht persoon', synoniemen: ['NEN 3140 VOP', 'NEN3140 VOP', 'NEN3140-VOP', 'Voldoende onderricht persoon NEN 3140'] },
  { typeNaam: 'Hoogwerker 1A', synoniemen: ['IPAF 1A', 'IPAF-1A', 'Hoogwerkercertificaat 1A', 'Hoogwerker cat 1A'] },
  { typeNaam: 'Hoogwerker 1B', synoniemen: ['IPAF 1B', 'IPAF-1B', 'Hoogwerkercertificaat 1B', 'Hoogwerker cat 1B'] },
  { typeNaam: 'Hoogwerker 3A', synoniemen: ['IPAF 3A', 'IPAF-3A', 'Hoogwerkercertificaat 3A', 'Hoogwerker cat 3A'] },
  { typeNaam: 'Hoogwerker 3B', synoniemen: ['IPAF 3B', 'IPAF-3B', 'Hoogwerkercertificaat 3B', 'Hoogwerker cat 3B'] },
  { typeNaam: 'Heftruck', synoniemen: ['Vorkheftruck', 'Heftruckcertificaat', 'Heftruckchauffeur', 'Vorkheftruckchauffeur'] },
  { typeNaam: 'Reach truck', synoniemen: ['Reachtruck', 'Reachtruckcertificaat'] },
  { typeNaam: 'Orderpicker', synoniemen: ['Orderpickercertificaat', 'Order picker'] },
]
